import { BffHttpClient, BFF_SERVICE_MAPPING } from './BffHttpClient'
import { LegacyHttpClient } from './LegacyHttpClient'
import { IHttpClient } from './IHttpClient'
import { AuthFlowHelper } from '@/utils/AuthFlowHelper'

/**
 * HTTP Client Factory - Creates appropriate HTTP client based on feature flags
 */
export class AttaHttpClientFactory {
	private static _shouldUseBffRouting: boolean | null = null
	/**
	 * Creates an HTTP client with appropriate routing strategy
	 * @param baseUrl The base URL for the service
	 * @param headers Optional headers configuration
	 */
	static async createAsync(baseUrl: string | undefined, headers?: any): Promise<IHttpClient> {
		if (!baseUrl) {
			throw new Error('Base URL is required')
		}

		if (this._shouldUseBffRouting === null) {
			try {
				this._shouldUseBffRouting = await AuthFlowHelper.isNewAuthFlowEnabled()
			} catch (error) {
				this._shouldUseBffRouting = false
			}
		}

		if (this._shouldUseBffRouting && this.isBffCompatibleService(baseUrl)) {
			return new BffHttpClient(baseUrl, headers)
		} else {
			return new LegacyHttpClient(baseUrl, headers)
		}
	}

	/**
	 * <Temporary for feature rollout control>
	 * Creates a synchronous-looking HTTP client that handles async resolution internally
	 * This allows existing code to work without changes while supporting the new routing
	 * whilst it depends on the feature flag to be resolved.
	 */
	static create(baseURL: string): IHttpClient {
		const httpPromise = this.createAsync(baseURL)

		return new Proxy({} as IHttpClient, {
			get(target, prop) {
				return async (...args: any[]) => {
					const http = await httpPromise
					const method = http[prop as keyof IHttpClient] as any
					if (typeof method === 'function') {
						return method.apply(http, args)
					}
					return method
				}
			}
		})
	}

	/**
	 * Check if the service is compatible with BFF routing
	 */
	private static isBffCompatibleService(baseUrl: string): boolean {
		return Object.keys(BFF_SERVICE_MAPPING).includes(baseUrl)
	}
}
